/**
 * Aviso do período de teste no topo das telas (chamado 7176).
 *
 * Discreto no começo do teste e com destaque nos últimos dias (o tom vem
 * pronto do backend em `trial.notice_level`). Nunca bloqueia: pode ser fechado
 * na sessão — e reaparece quando o tom sobe, porque a chave inclui o nível.
 * Com a assinatura já pedida, quem fala é o modal de pagamento pendente.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Sparkles, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { TRIAL_MEMBER_HINT, trialNoticeText, trialNoticeTitle } from '@/lib/trial-subscription';
import { cn } from '@/lib/utils';
import type { UserSubscription } from '@/services/account.service';

interface TrialNoticeBannerProps {
  subscription: UserSubscription | null | undefined;
}

const chaveDoFechamento = (subscription: UserSubscription): string =>
  `trial-notice-dismissed-${subscription.id}-${subscription.trial?.notice_level ?? ''}`;

const lerFechado = (chave: string): boolean => {
  try {
    return sessionStorage.getItem(chave) === 'true';
  } catch {
    return false;
  }
};

const gravarFechado = (chave: string): void => {
  try {
    sessionStorage.setItem(chave, 'true');
  } catch {
    // Sem armazenamento (aba privada): o aviso só fica fechado nesta tela.
  }
};

export function TrialNoticeBanner({ subscription }: Readonly<TrialNoticeBannerProps>) {
  const navigate = useNavigate();
  const [fechadas, setFechadas] = useState<string[]>([]);

  const trial = subscription?.trial;
  // Teste já vencido (rebaixamento adiado): não há "termina hoje" a anunciar.
  if (!subscription || !trial || trial.has_ended || subscription.pending_plan_id) return null;

  const chave = chaveDoFechamento(subscription);
  if (fechadas.includes(chave) || lerFechado(chave)) return null;

  const destaque = trial.notice_level !== 'info';
  const titular = subscription.is_billing_owner !== false;

  // Mesmo padrão visual da tarja de fatura vencida (SubscriptionWarningBanner):
  // degradê, ícone em círculo, título + descrição, botão branco e fechar. Só a
  // cor muda: marca no aviso informativo, laranja quando o fim se aproxima.
  return (
    // <output> tem o papel "status": leitores de tela anunciam o aviso.
    <output
      className={cn(
        'block bg-gradient-to-r text-white px-4 py-3 shadow-lg',
        'animate-in slide-in-from-top duration-300',
        destaque ? 'from-amber-500 to-orange-500' : 'from-primary to-teal-600',
      )}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-full">
            <Clock className="h-5 w-5" />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
            <span className="font-semibold">{trialNoticeTitle(trial)}</span>
            <span className="text-sm text-white/90">
              {trialNoticeText(trial, subscription.plan_name ?? 'seu plano')}
              {!titular && ` ${TRIAL_MEMBER_HINT}`}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {titular && (
            <Button
              size="sm"
              className={cn(
                'bg-white hover:bg-white/90 font-semibold shadow-sm',
                destaque ? 'text-orange-600' : 'text-primary',
              )}
              onClick={() => navigate('/account?tab=plan')}
            >
              <Sparkles className="h-4 w-4 mr-1" />
              Assinar agora
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="text-white/80 hover:text-white hover:bg-white/10"
            onClick={() => {
              gravarFechado(chave);
              setFechadas((atuais) => [...atuais, chave]);
            }}
            aria-label="Fechar aviso do período de teste"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </output>
  );
}

export default TrialNoticeBanner;
